import moment from 'moment';

import { pendingOrdersDatePlacedFormat } from '../constants/date-time.constants';
import lang from '../language';
import { CompanyLogoAndNameState } from '../models/fundamental/types';
import { SOROrderType } from '../models/gateway/types';

import {
  AccountTradeHistoryResponseData,
  MyStocksContent,
  OpenPosition,
  PositionType,
} from '../store/reporting/types';

import { CalculatedTradePrice } from '../store-util/TradePriceCache';

import {
  calcUnrealizedProfitPercent,
  formatCurrency,
  roundUnrealizedProfit,
  unrealizedPnL,
} from './DataHelpers';

import { TypedObject } from './types';

const getCachedPrice = (
  symbol: string,
  cache: Partial<Record<string, CalculatedTradePrice>>,
): CalculatedTradePrice | undefined => cache[symbol];

export const navigationButtonTextLong = lang.commonAccountBreakdownLong();
export const navigationButtonTextShort = lang.commonShort();

export const setPendingOrdersDatePlaced = (
  orderDate: string,
) => moment.utc(orderDate).format(pendingOrdersDatePlacedFormat);

const absoluteDateValue = (date: string) => new Date(date).valueOf();

export const sortPendingOrders = (pendingOrders: AccountTradeHistoryResponseData[]) => (
  [ ...pendingOrders ].sort((a, b) => (
    absoluteDateValue(a.expirationDate) !== absoluteDateValue(b.expirationDate)
      ? absoluteDateValue(b.expirationDate) - absoluteDateValue(a.expirationDate)
      : absoluteDateValue(b.orderDate) - absoluteDateValue(a.orderDate)
  ))
);

export const sortOpenPositions = (openPositions: OpenPosition[]) => (
  [ ...openPositions ].sort((a, b) => (
    absoluteDateValue(b.lastExecutionDateTime) - absoluteDateValue(a.lastExecutionDateTime)
  ))
);

// eslint-disable-next-line max-len
export const calculateMyStockContent = (activePosition: string, positions: OpenPosition[], currentPrice: Partial<Record<string, CalculatedTradePrice>>) => {
  const longPositions = getLongPositions(positions);
  const shortPositions = getShortPositions(positions);
  if (activePosition === navigationButtonTextLong) {
    return {
      data: longPositions,
      value: calculateTotalPostionsValue(positions, currentPrice).totalLongPos,
    };
  }
  if (activePosition === navigationButtonTextShort) {
    return {
      data: shortPositions,
      value: calculateTotalPostionsValue(positions, currentPrice).totalShortPos,
    };
  }

  return {
    data: positions,
    value: calculateTotalPostionsValue(positions, currentPrice).totalPositions,
  };
};

export const mapMyStocksTableContent = (
  sortedOpenPositions: OpenPosition[],
  cache: Partial<Record<string, CalculatedTradePrice>>,
  companyLogoAndName: TypedObject<CompanyLogoAndNameState>,
): MyStocksContent[] => {
  const tableContentPos: MyStocksContent[] = [];

  for (let index = 0; index < sortedOpenPositions.length; index += 1) {
    const { symbol, quantity, averageOpeningPrice, positionType } = sortedOpenPositions[index];
    const absoluteQuantity = Math.abs(quantity);
    const marketPrice = getCachedPrice(sortedOpenPositions[index].symbol, cache)?.currentPrice;
    const unrealizedProfitNumber = unrealizedPnL(quantity, marketPrice!, averageOpeningPrice);
    const unrealizedProfitPercent = calcUnrealizedProfitPercent(positionType, marketPrice!, averageOpeningPrice);

    tableContentPos.push({
      id: symbol,
      name: symbol,
      subtitle: companyLogoAndName[symbol]?.name,
      icon: companyLogoAndName[symbol]?.logo,
      quantity: absoluteQuantity,
      purchasePrice: formatCurrency(sortedOpenPositions[index].averageOpeningPrice),
      marketPrice: formatCurrency(marketPrice),
      unrealizedProfit: `${roundUnrealizedProfit(unrealizedProfitNumber, true)}
      (${Math.abs(Number(unrealizedProfitPercent))}%)`,
      buttons: '',
      positionType,
      unrealizedProfitNumber,
    });
  }
  return tableContentPos;
};

// eslint-disable-next-line max-len
export const setPendingOrdersPrice = (type: SOROrderType, orderRequestedPrice: number | null, orderStopPrice: number | null) => {
  switch (type) {
    case SOROrderType.Limit:
      return orderRequestedPrice;
    case SOROrderType.Stop:
      return orderStopPrice;
    default: return null;
  }
};

export const getLongPositions = (positions: OpenPosition[]): OpenPosition[] => (
  positions.filter(position => position.positionType === PositionType.Buy)
);

export const getShortPositions = (positions: OpenPosition[]): OpenPosition[] => (
  positions.filter(position => position.positionType === PositionType.Sell)
);

export const calculateTotalPostionsValue = (
  openPositions: OpenPosition[],
  marketPriceCache: Partial<Record<string, CalculatedTradePrice>>,
) => {
  const total = openPositions.reduce(
    (acc, val) => (val.positionType === PositionType.Buy
      ? {
        short: acc.short,
        long: acc.long + val.quantity * Number(getCachedPrice(val.symbol, marketPriceCache)?.currentPrice ?? 0),
      }
      : {
        long: acc.long,
        short: acc.short + Math.abs(val.quantity) * Number(getCachedPrice(val.symbol, marketPriceCache)?.currentPrice ?? 0),
      }),
    { long: 0, short: 0 },
  );

  return { totalLongPos: total.long, totalShortPos: total.short, totalPositions: total.long + total.short };
};
